import { useCallback, useMemo, useEffect, useRef } from 'react';

import { debounce } from 'lodash';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useEnv } from '@zenbusiness/application-commons-env';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { gql, useQueryClientRequest } from '@zenbusiness/application-commons-graphql';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import { useCaptureException, ZBError } from '@zenbusiness/application-commons-logging';

import { ENABLE_FLOW_STATE_PERSISTENCE } from '#client/constants/experimentFlags';
import {
  LOCAL_STORAGE_LEAD_UUID_KEY,
  LOCAL_STORAGE_VISIT_UUID_KEY
} from '#client/constants/localStorage';

const SAVE_FLOW_MUTATION = gql(`
  mutation SaveFlowState($input: FlowStateInput!) {
    saveFlowState(input: $input) {
      updatedAt
    }
  }
`);

function getError(error) {
  const selectedError = Array.isArray(error)
    ? error
    : error.httpError || error.fetchError || error.graphQLErrors;

  const firstError = Array.isArray(selectedError) ? selectedError[0] : selectedError;
  const zbError = new ZBError(firstError, {
    contextData: {
      details: {
        ...firstError
      }
    }
  });
  return zbError;
}

/**
 * Hook for saving flow state to the backend with debouncing
 *
 * @param {string} flowId - The unique identifier for the flow
 * @returns {object} Object containing:
 *   - debouncedSaveFlow: Debounced function to save flow state (1s delay)
 *   - cancelDebounce: Function to cancel any pending debounced saves
 *
 * @example
 * const [saveFlow, debouncedSaveFlow, cancelDebounce] = useSaveFlowState('flow-123');
 *
 * // Save with debounce
 * debouncedSaveFlow({ step: 'checkout', data: {...} });
 *
 * // Cancel pending saves
 * cancelDebounce();
 */

function useSaveFlowState(flowId) {
  const captureException = useCaptureException();
  const { accountUuid } = useAuthentication();
  const { IS_LOCAL, USE_LOCAL_SAVE_FLOWSTATE } = useEnv();
  const isExperimentEnabled = useExperimentFlag(ENABLE_FLOW_STATE_PERSISTENCE, false);
  const request = useQueryClientRequest();
  const abortControllerRef = useRef();
  const debouncedSaveFlowRef = useRef(null);

  const isEnvVarEnabled = USE_LOCAL_SAVE_FLOWSTATE === true;
  const isFlowStatePersistenceEnabled = IS_LOCAL
    ? isEnvVarEnabled && isExperimentEnabled
    : isExperimentEnabled;

  const saveFlow = useCallback(
    async (flowState) => {
      // Cancel previous request and create new controller
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      const visitUuid = window.localStorage.getItem(LOCAL_STORAGE_VISIT_UUID_KEY) ?? null;
      const leadUuid = window.localStorage.getItem(LOCAL_STORAGE_LEAD_UUID_KEY) ?? null;

      const variables = {
        input: {
          flowId,
          state: JSON.stringify(flowState),
          visitUuid,
          leadUuid,
          accountUuid: flowState.accountUuid ?? accountUuid
        }
      };

      const { error } = await request({
        query: SAVE_FLOW_MUTATION,
        variables,
        fetchOptionsOverrides: {
          signal: abortControllerRef.current.signal
        }
      });
      if (error) {
        captureException(getError(error));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const cancelDebounce = useCallback(() => {
    debouncedSaveFlowRef.current?.cancel?.();
  }, []);

  const debouncedSaveFlow = useMemo(() => {
    if (!isFlowStatePersistenceEnabled) {
      return cancelDebounce;
    }
    const debounced = debounce(saveFlow, 1000);
    debouncedSaveFlowRef.current = debounced;
    return debounced;
  }, [isFlowStatePersistenceEnabled, saveFlow, cancelDebounce]);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    []
  );

  // cancel debounce on unmount
  useEffect(
    () => () => {
      cancelDebounce();
    },
    [cancelDebounce]
  );

  return useMemoObject({ saveFlowState: debouncedSaveFlow, cancelSaveFlowState: cancelDebounce });
}

export default useSaveFlowState;
